import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, FileText, Download, CreditCard, CheckCircle2, AlertCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  paid: { label: "Paid", cls: "bg-green-500/10 text-green-400 border-green-500/30", icon: CheckCircle2 },
  sent: { label: "Awaiting payment", cls: "bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30", icon: Clock },
  overdue: { label: "Overdue", cls: "bg-red-500/10 text-red-400 border-red-500/30", icon: AlertCircle },
  draft: { label: "Draft", cls: "bg-[#333] text-[#888] border-[#333]", icon: FileText },
  cancelled: { label: "Cancelled", cls: "bg-[#333] text-[#888] border-[#333]", icon: FileText },
};

export default function CustomerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/customer-portal/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError("Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-white text-2xl font-outfit mb-2">Link not valid</h1>
        <p className="text-[#888] text-sm">This portal link is invalid or has been revoked. Please contact us if you think this is a mistake.</p>
      </div>
    </div>
  );

  const { customer, invoices, seller } = data;
  const unpaid = invoices.filter(i => i.status !== "paid" && i.status !== "cancelled" && !i.credited && i.type !== "credit_note");
  const paid = invoices.filter(i => i.status === "paid" || i.type === "credit_note");
  const other = invoices.filter(i => !unpaid.includes(i) && !paid.includes(i));

  const totalUnpaid = unpaid.reduce((acc, i) => {
    acc[i.currency] = (acc[i.currency] || 0) + (i.total_amount || 0);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] px-6 md:px-12 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <Link to="/" className="font-outfit text-xl tracking-tight">wedowedo</Link>
          <div className="text-right">
            <p className="text-[10px] font-mono text-[#666] tracking-wider">CUSTOMER PORTAL</p>
            <p className="text-sm text-white mt-1">{customer.company_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Greeting */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-outfit mb-2">Hello {customer.contact_name?.split(" ")[0] || "there"},</h1>
          <p className="text-[#888] text-sm max-w-xl">
            Here's an overview of all your invoices with WeDoWeDo. Pay open invoices instantly with card or download receipts for your records.
          </p>
        </div>

        {/* Summary */}
        {Object.keys(totalUnpaid).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm p-5">
              <p className="text-[10px] font-mono text-[#666] tracking-wider mb-2">OPEN BALANCE</p>
              {Object.entries(totalUnpaid).map(([cur, amt]) => (
                <p key={cur} className="text-2xl font-outfit text-[#00F0FF]">
                  {amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-sm text-[#666]">{cur}</span>
                </p>
              ))}
            </div>
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm p-5">
              <p className="text-[10px] font-mono text-[#666] tracking-wider mb-2">UNPAID INVOICES</p>
              <p className="text-2xl font-outfit">{unpaid.length}</p>
            </div>
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm p-5">
              <p className="text-[10px] font-mono text-[#666] tracking-wider mb-2">PAID THIS YEAR</p>
              <p className="text-2xl font-outfit text-green-400">{paid.length}</p>
            </div>
          </div>
        )}

        {/* Unpaid */}
        {unpaid.length > 0 && (
          <Section title="Open invoices" count={unpaid.length}>
            {unpaid.map(inv => <InvoiceRow key={inv.id} inv={inv} allowPay />)}
          </Section>
        )}

        {/* Paid */}
        {paid.length > 0 && (
          <Section title="Paid & credits" count={paid.length}>
            {paid.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}
          </Section>
        )}

        {/* Other (drafts, cancelled) */}
        {other.length > 0 && (
          <Section title="Other" count={other.length}>
            {other.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}
          </Section>
        )}

        {invoices.length === 0 && (
          <div className="text-center py-20 border border-dashed border-[#1a1a1a] rounded-sm">
            <FileText className="w-10 h-10 text-[#333] mx-auto mb-3" />
            <p className="text-[#666] text-sm">No invoices yet.</p>
          </div>
        )}

        {/* Footer with seller details */}
        <footer className="mt-16 pt-6 border-t border-[#1a1a1a] text-[11px] text-[#555] space-y-1">
          <p>{seller.name} · Reg: {seller.reg} · VAT: <span className="text-[#888]">{seller.vat}</span> · {seller.address}</p>
          {customer.vat_number && <p>Your VAT: {customer.vat_number}{customer.org_number ? ` · Org: ${customer.org_number}` : ""}</p>}
        </footer>
      </main>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-xl font-outfit">{title}</h2>
        <span className="text-[10px] font-mono text-[#666] tracking-wider">{count} ITEM{count === 1 ? "" : "S"}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function InvoiceRow({ inv, allowPay }) {
  const meta = STATUS_META[inv.status] || STATUS_META.draft;
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/pay/${inv.id}/checkout`, { origin_url: origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start payment");
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/pay/${inv.id}/pdf`, "_blank");
  };

  const invNumber = inv.id ? inv.id.slice(0, 8).toUpperCase() : "-";
  const isCredit = inv.type === "credit_note";

  return (
    <div className="bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#222] rounded-sm p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 transition-colors">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`w-9 h-9 rounded-sm border flex items-center justify-center flex-shrink-0 ${meta.cls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-sm text-white">{isCredit ? "CREDIT" : "INV"}-{invNumber}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono border ${meta.cls}`}>{meta.label}</span>
            {inv.reverse_charge && <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">Reverse charge</span>}
          </div>
          <p className="text-xs text-[#666] mt-1">
            {inv.invoice_date || inv.created_at?.slice(0, 10)}{inv.due_date ? ` · Due ${inv.due_date}` : ""}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 md:gap-6 flex-shrink-0">
        <div className="text-right min-w-[120px]">
          <p className={`font-outfit text-lg ${isCredit ? "text-[#666]" : "text-white"}`}>
            {(inv.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] font-mono text-[#666]">{inv.currency}</p>
        </div>

        <div className="flex gap-2">
          <button onClick={downloadPdf} data-testid={`download-pdf-${inv.id}`} className="w-9 h-9 border border-[#1a1a1a] hover:border-[#333] rounded-sm flex items-center justify-center text-[#888] hover:text-white transition-colors" title="Download PDF">
            <Download className="w-4 h-4" />
          </button>
          {allowPay && (
            <button onClick={pay} disabled={busy} data-testid={`pay-invoice-${inv.id}`} className="px-4 h-9 bg-[#00F0FF] text-black text-sm font-medium rounded-sm hover:bg-[#00F0FF]/90 disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Pay
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
